import React from 'react';
import { useTheme } from 'Components/ComponentLibrary/Styles';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import useMobileLayout from 'Hooks/useMobileLayout';
import {
  FDPaper, FDContentContainer, FDBox, FDFacetLogo, FDStack,
} from '@facet-wealth/design';
import classNames from 'classnames';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import { useLayoutContext } from 'Context/LayoutContext';
import { dataAttrs } from 'Helpers/Testing';

const WorkflowLayoutContext = React.createContext(undefined);

function WorkflowLayoutProvider({ children }) {
  const [isContentShowing, setIsContentShowing] = React.useState(true);

  const memoizedValue = React.useMemo(() => {
    return {
      isContentShowing,
      setIsContentShowing,
    };
  }, [isContentShowing, setIsContentShowing]);

  return (
    <WorkflowLayoutContext.Provider value={memoizedValue}>
      {children}
    </WorkflowLayoutContext.Provider>
  );
}
WorkflowLayoutProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export const useWorkflowLayoutContext = () => {
  const context = React.useContext(WorkflowLayoutContext);

  if (context === undefined) {
    throw new Error('useWorkflowLayout must be used within a WorkflowLayoutProvider');
  }

  return context;
};

const useStyles = buildUseStyles('WorkflowLayout', (() => ({
  rootHidden: {
    display: 'none',
  },
})));

const WorkflowLayoutContent = ({
  children, appearance, resource, alwaysShowFacetLogo,
}) => {
  const { isContentShowing } = useWorkflowLayoutContext();
  const { isNavigationVisible } = useLayoutContext();
  const isMobile = useMobileLayout();
  const classes = useStyles();

  // If the nav isn't visible, members should be able to see the Facet logo to orient themselves
  const showFacetLogo = alwaysShowFacetLogo || !isNavigationVisible;

  const theme = useTheme();
  useBackgroundColor(isMobile ? undefined : theme.palette.facet.lightGray);

  let content = (
    <FDBox paddingY={4}>
      <FDContentContainer maxWidth={'sm'}>
        <FDStack direction={'column'} spacing={4}>
          {showFacetLogo && <FDFacetLogo size={'md'} />}
          <FDPaper appearance={appearance} color="subtle">
            <FDBox padding={4}>
              {children}
            </FDBox>
          </FDPaper>
        </FDStack>
      </FDContentContainer>
    </FDBox>
  );

  if (isMobile) {
    content = (
      <FDBox paddingY={2}>
        <FDStack direction={'column'} spacing={2}>
          {showFacetLogo && <FDFacetLogo size={'md'} />}
          <FDBox paddingY={2} paddingX={1}>
            {children}
          </FDBox>
        </FDStack>
      </FDBox>
    );
  }

  return (
    <div
      className={classNames({ [classes.rootHidden]: !isContentShowing })}
      {...dataAttrs('workflow-layout', resource)}
    >
      {content}
    </div>
  );
};

WorkflowLayoutContent.propTypes = {
  appearance: PropTypes.string,
  resource: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  alwaysShowFacetLogo: PropTypes.bool,
};

WorkflowLayoutContent.defaultProps = {
  appearance: 'flat',
  resource: undefined,
  alwaysShowFacetLogo: false,
};

export const WorkflowLayout = ({
  children, appearance, resource, alwaysShowFacetLogo,
}) => (
  <WorkflowLayoutProvider>
    <WorkflowLayoutContent
      appearance={appearance}
      resource={resource}
      alwaysShowFacetLogo={alwaysShowFacetLogo}
    >
      {children}
    </WorkflowLayoutContent>
  </WorkflowLayoutProvider>
);

WorkflowLayout.propTypes = {
  appearance: PropTypes.string,
  resource: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  alwaysShowFacetLogo: PropTypes.bool,
};

WorkflowLayout.defaultProps = {
  resource: undefined,
  appearance: 'flat',
  alwaysShowFacetLogo: false,
};
